import { useState } from 'react';
import LessonCard from './components/LessonCard';
import { lessonsData } from './data/lessonsData';
import SEOHead from '../../../components/SEOHead';
export default function Grade4Page() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets'>('lessons');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50">
      <SEOHead title="Grade 4 Math New York | Fractions Decimals Multi-digit | Augustine Anyimadu-Ahenkae" description="Grade 4 math lessons covering multi-digit operations, equivalent fractions, decimals, angles, and lines. Common Core Grade 4 aligned interactive lessons and printable worksheets." canonical="/grades/grade-4" keywords="Grade 4 math, fractions decimals, multi-digit multiplication, Common Core grade 4, 4th grade math" jsonLd={{'@context':'https://schema.org','@type':'Course',name:'Grade 4 Math',url:`${siteUrl}/grades/grade-4`,provider:{'@type':'Person',name:'Augustine Anyimadu-Ahenkae'},educationalLevel:'Grade 4'}} />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center">
              <i className="ri-number-4 text-3xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Grade 4 Math</h1>
              <p className="text-gray-600 mt-1">Advancing skills in multi-digit operations and fractions</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setActiveTab('lessons')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'lessons'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Lessons
            </button>
            <button
              onClick={() => setActiveTab('worksheets')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'worksheets'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Worksheets
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'lessons' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessonsData.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} />
            ))}
          </div>
        )}

        {activeTab === 'worksheets' && (
          <div className="space-y-8">
            {/* Worksheet Preview Banner */}
            <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-2xl p-8 text-white">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-file-text-line text-2xl"></i>
                    <h2 className="text-2xl font-bold">Practice Worksheets Available</h2>
                  </div>
                  <p className="text-rose-100 mb-6">
                    Download our free sample worksheet and explore more practice materials for Grade 4
                  </p>
                  
                  {/* Free Sample Card */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 mb-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-green-400 text-green-900 rounded-md text-xs font-bold">
                            FREE SAMPLE
                          </span>
                        </div>
                        <h3 className="text-lg font-bold mb-2">Fractions & Decimals Fundamentals</h3>
                        <p className="text-rose-100 text-sm mb-3">
                          Compare, order, and perform operations with fractions and decimals
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <i className="ri-file-line"></i>
                            10 pages
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-checkbox-circle-line"></i>
                            40 problems
                          </span>
                        </div>
                      </div>
                      <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                        <i className="ri-download-cloud-line text-3xl"></i>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => alert('Download will start shortly!')}
                      className="px-6 py-3 bg-white text-pink-600 rounded-lg font-semibold hover:bg-rose-50 transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-download-line"></i>
                      Download Free Sample
                    </button>
                    <a
                      href="/worksheets?category=grade-4"
                      className="px-6 py-3 bg-pink-700 text-white rounded-lg font-semibold hover:bg-pink-800 transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                      View All Worksheets
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Membership Upsell */}
            <div className="bg-white rounded-xl p-8 border-2 border-pink-200">
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vip-crown-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Unlock 75+ Grade 4 Worksheets
                </h3>
                <p className="text-gray-600 mb-6">
                  Get access to our complete collection covering multi-digit operations, fractions, decimals, geometry, and measurement with comprehensive answer keys
                </p>
                <a
                  href="/#membership"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-lg font-semibold hover:from-rose-600 hover:to-pink-700 transition-all whitespace-nowrap"
                >
                  Choose Your Plan
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}